import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import axios from 'axios';
import {
    GET_PTT_FETCH_AUDIO_MESSAGES_GROUP_URL,
    POST_PTT_FETCH_AUDIO_MESSAGES_FILTER_URL,
} from '../../../../const/const';
import { Device } from '../../../../types/Device';
import { User } from '../../../../types/User';
import { PTTGroup } from '../../../../types/PTTGroup';

export type MessageDirection = 'incoming' | 'outgoing';

export interface PTTMessage {
    id: string; // Уникальный идентификатор сообщения
    audioUrl: string;
    dataBlob?: Blob; // Сделаем необязательным, так как с бекенда может приходить только URL
    direction: MessageDirection;
    sender: {
        id: string;
        name: string | undefined;
        type: 'device' | 'operator';
    };
    recipient: {
        id: string;
        type: 'device' | 'group' | 'operator';
        name?: string;
    };
    timestamp: string;
    isPlayed: boolean | undefined;
    fileUrl?: string; // URL для загрузки с сервера
    audioBlobUrl?: string;
    isLocal?: boolean;
    isPending?: boolean;
    uid?: string;
    date_time?: string;
    group?: PTTGroup;
}

// Модифицируем стор для работы с новым форматом сообщений
interface PTTMessageState {
    messages: PTTMessage[];
    unreadCounts: Record<string, number>;
    messagesArchive: PTTMessage[];
    playQueue: PTTMessage[]; // Добавляем очередь для новых сообщений
    isPlaying: boolean; // Флаг текущего воспроизведения
    currentPlayingId: null | string;
    currentAudioElement: HTMLAudioElement | null;
    isManualPlay: boolean;
    pausedQueue: PTTMessage[]; // Сохраняем очередь при паузе
    isQueuePaused: boolean; // Флаг паузы очереди
}

interface PTTMessageActions {
    addMessage: (msg: PTTMessage) => void;
    addMessages: (msgs: PTTMessage[]) => void; // Для массового добавления с сервера
    markMessageAsPlayed: (id: string) => void;
    clearMessages: () => void;
    clearArchiveMessages: () => void;
    fetchMessages: (
        props: {
            data: {
                deviceUID?: string;
                groupUID?: string;
                operatorUID?: string;
                recipient_uid?: string;
                receiver_type?: string;
                receiver_uid?: string;
                recipient_type?: string;
                ptt_group_uid?: string;
                start?: string;
                end?: string;
            };
            userLogin: string;
            SmartDVRToken: string;
            operators?: User[];
            users?: User[];
            devices?: Device[];
            pttGroups?: PTTGroup[];
            page?: number;
            count?: number;
        },
        archive?: boolean,
    ) => any;
    fetchMessagesByGroup: (props: {
        groupUID: string;
        userLogin: string;
        SmartDVRToken: string;
        page?: number;
        operators?: User[];
        users?: User[];
        devices?: Device[];
        count?: number;
    }) => void;
    loadAudioBlob: (messageId: string, userLogin: string, token: string) => Promise<void>;
    incrementUnread: (targetId: string) => void;
    resetUnread: (targetId: string) => void;
    addToPlayQueue: (message: PTTMessage) => void;
    startPlayingFromQueue: () => void;
    onPlaybackFinished: () => void;
    clearPlayQueue: () => void;
    setIsPlaying: (isPlaying: boolean) => void;
    handleManualPlay: (messageId: string) => void;
    pauseCurrentAudio: () => void;
    setCurrentAudioElement: (element: HTMLAudioElement | null) => void;
    setIsManualPlaying: (isManual: boolean) => void;
    setCurrentPlayingId: (id: string | null) => void;
    pauseQueue: () => void; // Приостановить очередь
    resumeQueue: () => void; // Возобновить очередь
    setPauseQueue: (pauseQueue: PTTMessage) => void;
}

const usePTTMessageStore = create<PTTMessageState & PTTMessageActions>()(
    persist(
        (set, get) => ({
            messages: [],
            messagesArchive: [],
            unreadCounts: {} as Record<string, number>,
            playQueue: [],
            isPlaying: false,
            currentPlayingId: null,
            currentAudioElement: null,
            isManualPlay: false,
            pausedQueue: [],
            isQueuePaused: false,

            addMessage: (msg) => {
                set((state) => {
                    // const exists = state.messages.some((m) => m.id === msg.id);
                    const exists = state.messages.some(
                        (m) => m.id === msg.id && m.timestamp === msg.timestamp,
                    );
                    if (exists) {
                        console.log('Сообщение уже существует в хранилище:', msg.id);
                        return state;
                    }
                    // const messages = exists
                    //     ? state.messages.map((m) => (m.id === msg.id ? msg : m))
                    //     : [...state.messages, msg];
                    return { messages: [...state.messages, msg] };
                    // return { messages };
                });
            },

            addMessages: (msgs) => {
                set((state) => {
                    const existingIds = new Set(state.messages.map((m) => m.id));
                    const newMessages = msgs?.filter((msg) => !existingIds.has(msg.id)) ?? [];
                    return { messages: [...state.messages, ...newMessages] };
                });
            },

            markMessageAsPlayed: (id) => {
                set((state) => ({
                    messages: state.messages.map((m) =>
                        m.id === id ? { ...m, isPlayed: true } : m,
                    ),
                }));
            },

            clearMessages: () => set({ messages: [] }),
            clearArchiveMessages: () => set({ messagesArchive: [] }),
            fetchMessages: async (props, archive) => {
                // Очищаем только если это не архив
                if (!archive) {
                    set((state) => ({
                        messages: state.messages.filter(
                            (m) =>
                                !(
                                    props.data.deviceUID &&
                                    (m.recipient.id === props.data.deviceUID ||
                                        m.sender.id === props.data.deviceUID)
                                ) &&
                                !(
                                    props.data.groupUID &&
                                    (m.recipient.id === props.data.groupUID ||
                                        m.group?.uid === props.data.groupUID)
                                ),
                        ),
                    }));
                }

                const response = await axios.post(
                    POST_PTT_FETCH_AUDIO_MESSAGES_FILTER_URL(props.page ?? 1, props.count ?? 40),
                    {
                        deviceUID: props.data.deviceUID || undefined,
                        groupUID: props.data.groupUID || undefined,
                        operatorUID: props.data.operatorUID || undefined,
                        start: props.data.start || '2025-01-01T00:00:00Z',
                        end: props.data.end || '2099-01-01T00:00:00Z',
                    },
                    {
                        headers: {
                            SmartDVRLogin: props.userLogin,
                            SmartDVRToken: props.SmartDVRToken,
                        },
                    },
                );

                const formattedMessages = response?.data?.data?.map((msg: any): PTTMessage => {
                    const getSafeId = (obj: any): string => obj?.uid || obj?.UID || obj?.id || '';

                    // Находим отправителя
                    let sender;
                    // let sender = props.devices?.find(d => d.UID === msg.receiver_uid) ||
                    //     props.operators?.find(u => u.uid === msg.receiver_uid) ||
                    //     props.users?.find(u => u.uid === msg.receiver_uid);
                    if (msg.receiver_type == 'device') {
                        sender = props?.devices?.find((d) => d.UID === msg.receiver_uid)!;
                    } else {
                        sender = props?.operators?.find((user) => user.uid === msg.receiver_uid)!;
                        // ?.concat(props?.users || [])
                        if (!sender) {
                            sender = props?.users?.find((user) => user.uid === msg.receiver_uid)!;
                        }
                    }

                    // Находим получателя
                    let recipient;
                    if (msg.recipient_type === 'device') {
                        recipient = props.devices?.find((d) => d.UID === msg.recipient_uid);
                    } else if (msg.recipient_type === 'operator') {
                        recipient =
                            props.operators?.find((u) => u.uid === msg.recipient_uid) ||
                            props.users?.find((u) => u.uid === msg.recipient_uid);
                    } else {
                        recipient = props.pttGroups?.find((g) => g.uid === msg.recipient_uid);

                        if (msg.recipient_uid === '00000000-0000-0000-0000-000000000000') {
                            recipient = {
                                uid: '00000000-0000-0000-0000-000000000000',
                                name: 'unsigned',
                                type: 'group',
                            };
                        }
                    }

                    // Определяем группу, если есть
                    const group = props.pttGroups?.find((g) => g.uid === msg.ptt_group_uid);

                    return {
                        id: msg.uid,
                        audioUrl: msg.file_url,
                        direction: msg.receiver_type === 'device' ? 'incoming' : 'outgoing',
                        sender: {
                            id: getSafeId(sender),
                            name:
                                sender?.name ||
                                (msg.receiver_type === 'operator'
                                    ? 'Оператор (исходный отправитель не найден)'
                                    : 'Устройство (исходный отправитель не найден)'),
                            type: msg.receiver_type === 'operator' ? 'operator' : 'device',
                        },
                        recipient: {
                            id: getSafeId(recipient),
                            type: msg.recipient_type,
                            name:
                                recipient?.name ||
                                (msg.recipient_type === 'operator'
                                    ? 'Оператор'
                                    : msg.recipient_type === 'device'
                                      ? 'Устройство (исходный получатель не найден)'
                                      : 'Группа (исходный получатель не найден)'),
                        },
                        timestamp: msg.date_time,
                        isPlayed: archive, // Архивные считаем проигранными
                        fileUrl: msg.file_url,
                        group: group,
                    };
                });

                if (archive) {
                    set({ messagesArchive: formattedMessages ?? [] });
                } else {
                    set({ messages: [...get().messages, ...(formattedMessages ?? [])] });
                }

                return [formattedMessages, response];
            },

            fetchMessagesByGroup: async (props) => {
                set((state) => ({
                    messages: state?.messages?.filter((m) => m?.recipient?.id !== props.groupUID),
                }));

                // get().messages = [];

                const response = await axios.get(
                    GET_PTT_FETCH_AUDIO_MESSAGES_GROUP_URL(
                        props.groupUID,
                        props.page ?? 1,
                        props.count ?? 40,
                    ),
                    {
                        headers: {
                            SmartDVRLogin: props.userLogin,
                            SmartDVRToken: props.SmartDVRToken,
                        },
                    },
                );

                const formattedMessages = response?.data?.data?.map((msg: any): PTTMessage => {
                    const device = props?.devices?.find((d) => d.UID === msg.receiver_uid);
                    let user = props?.operators?.find((user) => user.uid === msg.receiver_uid);

                    if (!user) {
                        user = props?.users?.find((user) => user.uid === msg.receiver_uid);
                    }

                    const recipientType =
                        msg.recipient_type === 'group'
                            ? 'group'
                            : msg.recipient_type === 'operator'
                              ? 'operator'
                              : 'device';

                    return {
                        id: msg.uid,
                        audioUrl: msg.file_url,
                        direction: msg.receiver_type === 'device' ? 'incoming' : 'outgoing',
                        sender: {
                            id: msg.sender_uid || msg.receiver_uid,
                            name:
                                msg.receiver_type == 'operator'
                                    ? user?.name
                                    : device?.name || msg.sender_name || 'Device',
                            type: msg.sender_type === 'operator' ? 'operator' : 'device',
                        },
                        recipient: {
                            id: msg.recipient_uid,
                            type: recipientType,
                        },
                        timestamp: msg?.date_time || '',
                        isPlayed: true, // Предполагаем что загруженные с сервера сообщения уже прослушаны
                        fileUrl: msg.file_url,
                    };
                });

                get().addMessages(formattedMessages);
            },
            loadAudioBlob: async (messageId, userLogin, token) => {
                const message = get().messages.find((m) => m.id === messageId);
                if (!message || message.audioBlobUrl) return;

                try {
                    const response = await fetch(message.audioUrl, {
                        headers: {
                            SmartDVRLogin: userLogin,
                            SmartDVRToken: token,
                        },
                    });

                    if (!response.ok) throw new Error('Failed to fetch audio');

                    const blob = await response.blob();
                    const blobUrl = URL.createObjectURL(blob);

                    set((state) => ({
                        messages: state.messages.map((m) =>
                            m.id === messageId ? { ...m, audioBlobUrl: blobUrl } : m,
                        ),
                    }));
                } catch (error) {
                    console.error('Error loading audio blob:', error);
                }
            },
            incrementUnread: (targetId: string) =>
                set((state) => ({
                    unreadCounts: {
                        ...state.unreadCounts,
                        [targetId]: (state.unreadCounts[targetId] || 0) + 1,
                    },
                })),
            resetUnread: (targetId: string) =>
                set((state) => ({
                    unreadCounts: {
                        ...state.unreadCounts,
                        [targetId]: 0,
                    },
                })),
            // Добавляем сообщение в очередь
            addToPlayQueue: (message) => {
                set((state) => {
                    if (
                        state.playQueue.some(
                            (m) => m.id === message.id && m.timestamp === message.timestamp,
                        )
                    ) {
                        return state;
                    }
                    //
                    // if (state.playQueue.some((m) => m.id === message.id)) {
                    //     return state;
                    // }
                    // Если уже воспроизводится аудио - просто добавляем в очередь
                    if (state.isPlaying) {
                        return {
                            playQueue: [...state.playQueue, message],
                            unreadCounts: {
                                ...state.unreadCounts,
                                [message.recipient.id]:
                                    (state.unreadCounts[message.recipient.id] || 0) + 1,
                            },
                        };
                    }
                    // Если ничего не воспроизводится - добавляем и запускаем
                    return {
                        // playQueue: [...state.playQueue, message],
                        playQueue: [message],
                        unreadCounts: {
                            ...state.unreadCounts,
                            [message.recipient.id]:
                                (state.unreadCounts[message.recipient.id] || 0) + 1,
                        },
                    };
                });

                if (!get().isPlaying) {
                    get().startPlayingFromQueue();
                }
            },

            // Начать воспроизведение из очереди
            startPlayingFromQueue: () => {
                const { playQueue } = get();
                if (playQueue.length === 0 || get().isPlaying) return;

                const nextMessage = playQueue[0];
                set({
                    isPlaying: true,
                    currentPlayingId: playQueue[0].id,
                    isManualPlay: false, // Автоматическое воспроизведение
                });

                // Даем время на рендер аудио элемента
                const tryPlay = () => {
                    const audioElement = document.getElementById(
                        `audio-${nextMessage.id}`,
                    ) as HTMLAudioElement;
                    if (audioElement) {
                        audioElement
                            .play()
                            .then(() => {
                                set({ currentAudioElement: audioElement });
                            })
                            .catch((error) => {
                                console.error('Playback failed:', error);
                                // Пробуем еще раз через 500мс
                                setTimeout(tryPlay, 500);
                            });
                    } else {
                        // Если элемент еще не появился, ждем
                        setTimeout(tryPlay, 100);
                    }
                };

                tryPlay();
            },

            // Когда воспроизведение завершено
            // onPlaybackFinished: () => {
            //     const { isManualPlay, isQueuePaused, resumeQueue } = get();
            //
            //     if (isManualPlay) {
            //         // После ручного прослушивания возобновляем очередь
            //         set({
            //             isPlaying: false,
            //             currentPlayingId: null,
            //             currentAudioElement: null,
            //             isManualPlay: false,
            //         });
            //         resumeQueue();
            //     } else if (isQueuePaused) {
            //         // Если очередь на паузе - ничего не делаем
            //         set({
            //             isPlaying: false,
            //             currentPlayingId: null,
            //             currentAudioElement: null,
            //         });
            //     } else {
            //         // Стандартное поведение для очереди
            //         const { playQueue } = get();
            //         set({
            //             isPlaying: false,
            //             currentPlayingId: null,
            //             currentAudioElement: null,
            //             playQueue: playQueue.slice(1),
            //         });
            //
            //         if (playQueue.length > 1) {
            //             get().startPlayingFromQueue();
            //         }
            //     }
            // },

            onPlaybackFinished: () => {
                const { isManualPlay, playQueue } = get();
                set({
                    isPlaying: false,
                    currentPlayingId: null,
                    currentAudioElement: null,
                    isManualPlay: false,
                    // playQueue: isManualPlay ? playQueue : playQueue.slice(1),
                });

                if (!isManualPlay && playQueue.length > 0) {
                    set({ playQueue: playQueue.slice(1) });
                    get().startPlayingFromQueue();
                }
                // if (!isManualPlay && playQueue.length > 0) {
                //     get().startPlayingFromQueue();
                // }
            },

            pauseCurrentAudio: () => {
                const { currentAudioElement } = get();
                if (currentAudioElement) {
                    currentAudioElement.pause();
                }
                set({
                    isPlaying: false,
                    currentPlayingId: null,
                    currentAudioElement: null,
                });
            },

            // handleManualPlay: (messageId) => {
            //     const { pauseCurrentAudio, pauseQueue } = get();
            //     pauseCurrentAudio();
            //     pauseQueue();
            //     set({
            //         isPlaying: true,
            //         currentPlayingId: messageId,
            //         isManualPlay: true,
            //     });
            // },
            handleManualPlay: (messageId) => {
                const { pauseCurrentAudio } = get();
                pauseCurrentAudio();
                set({
                    isPlaying: true,
                    currentPlayingId: messageId,
                    isManualPlay: true,
                    playQueue: [],
                });
            },
            setIsManualPlaying: (isManual) => set({ isManualPlay: isManual }),
            setIsPlaying: (isPlaying) => {
                set({
                    isPlaying: isPlaying,
                });
            },

            clearPlayQueue: () =>
                set({
                    playQueue: [],
                    isPlaying: false,
                    // currentPlayingId: null,
                    // currentAudioElement: null,
                }),
            setCurrentAudioElement: (element) => set({ currentAudioElement: element }),
            setCurrentPlayingId: (id) => set({ currentPlayingId: id }),

            pauseQueue: () => {
                const { playQueue, isPlaying } = get();
                set({
                    isQueuePaused: true,
                    pausedQueue: isPlaying ? playQueue : [],
                    isPlaying: false,
                    currentPlayingId: null,
                });
            },

            resumeQueue: () => {
                const { pausedQueue } = get();
                if (pausedQueue.length > 0) {
                    set({
                        playQueue: pausedQueue,
                        pausedQueue: [],
                        isQueuePaused: false,
                        isPlaying: true,
                        currentPlayingId: pausedQueue[0].id,
                    });
                    get().startPlayingFromQueue();
                } else {
                    set({
                        isQueuePaused: false,
                    });
                }
            },
            setPauseQueue: (message) => {
                const { pausedQueue } = get();
                set({
                    pausedQueue: [...pausedQueue, message],
                });
            },
        }),

        {
            name: 'ptt-messages-storage',
            partialize: (state) => ({
                // messages: state.messages,
                // unreadCounts: state.unreadCounts,
                // messagesArchive: state.messagesArchive,
            }),
        },
    ),
);

export default usePTTMessageStore;
